import { faCaretRight, IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { cva } from 'class-variance-authority';
import * as React from 'react';

import { Skeleton } from '../Skeleton';
import { Theme, ThemeEnum } from '../types';
import { cloneChildren } from '../utils/cloneChildren';
import { cn } from '../utils/cn';
import { iconStyles, iconSubtleStyle } from '.';

/**
 * Displays a menu to the user, such as a set of actions or functions, triggered by a button.
 * @see {@link https://stylus.design/dropdown}
 */
export const DropdownMenu = DropdownMenuPrimitive.Root;

/**
 * The button that toggles the dropdown menu. By default, the `DropdownMenu.Content` will position itself against the trigger.
 * @see {@link https://stylus.design/dropdown}
 */
export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

/**
 * Used to group multiple `DropdownMenuItems`.
 * @see {@link https://stylus.design/dropdown}
 */
export const DropdownMenuGroup = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Group> & {
    theme?: Theme;
  }
>(({ className, theme, children, ...props }, ref) => (
  <DropdownMenuPrimitive.Group ref={ref} className={className} {...props}>
    {cloneChildren(children, { theme })}
  </DropdownMenuPrimitive.Group>
));

/**
 * When used, portals the content part into the body.
 * @see {@link https://stylus.design/dropdown}
 */
export const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

/**
 * Contains all the parts of a submenu.
 * @see {@link https://stylus.design/dropdown}
 */
export const DropdownMenuSub: React.FC<
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Sub> & {
    theme?: Theme;
  }
> = ({ theme, children, ...props }) => (
  <DropdownMenuPrimitive.Sub {...props}>
    {cloneChildren(children, { theme })}
  </DropdownMenuPrimitive.Sub>
);

/**
 * Used to group multiple `DropdownMenuRadioItem`s.
 * @see {@link https://stylus.design/components/dropdown-menu#radio-group}
 */
export const DropdownMenuRadioGroup = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioGroup>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioGroup> & {
    theme?: Theme;
  }
>(({ className, theme, children, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioGroup ref={ref} className={className} {...props}>
    {cloneChildren(children, { theme })}
  </DropdownMenuPrimitive.RadioGroup>
));

export const contentStyles = cva(
  'data-[state=open]:animate-in data-[state=closed]:animate-out min-w-[var(--radix-dropdown-menu-trigger-width)] data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 overflow-x-hidden rounded-xl py-2 max-h-[calc(var(--radix-dropdown-menu-content-available-height)_-_8px)]',
  {
    variants: {
      theme: {
        dark: 'bg-slate-800 shadow-popover',
        light: 'bg-white shadow-popoverLight',
      },
    },
    defaultVariants: {
      theme: ThemeEnum.Dark,
    },
  },
);

export interface DropdownMenuSubContentProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent> {
  theme?: Theme;
}

/**
 * The component that pops out when a submenu is open. Must be rendered inside `DropdownMenuSub`.
 * @see {@link https://stylus.design/components/dropdown-menu#submenus}
 */
export const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  DropdownMenuSubContentProps
>(({ className, children, theme = ThemeEnum.Dark, ...props }, ref) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.SubContent
      ref={ref}
      className={cn(contentStyles({ theme }), className)}
      sideOffset={-8}
      alignOffset={-8}
      {...props}
    >
      {cloneChildren(children, { theme })}
    </DropdownMenuPrimitive.SubContent>
  </DropdownMenuPrimitive.Portal>
));

const DropdownMenuSkeleton = ({ theme }: { theme?: Theme }) => (
  <div className='flex flex-col space-y-4 px-4 py-2'>
    <div className='flex items-center space-x-2'>
      <Skeleton className='h-4 w-[88%] rounded-md' theme={theme} />
    </div>
    <div className='flex items-center space-x-2'>
      <Skeleton className='h-4 w-[96%] rounded-md' theme={theme} />
    </div>
    <div className='flex items-center space-x-2'>
      <Skeleton className='h-4 w-[90%] rounded-md' theme={theme} />
    </div>
  </div>
);

export interface DropdownMenuContentProps
  extends React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> {
  theme?: Theme;
  container?: HTMLElement;
  isLoading?: boolean;
}
/**
 * The component that pops out when the dropdown menu is open.
 * @see {@link https://stylus.design/dropdown}
 */
export const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  DropdownMenuContentProps
>(
  (
    { children, className, container, isLoading, sideOffset = 4, theme = ThemeEnum.Dark, ...props },
    ref,
  ) => (
    <DropdownMenuPrimitive.Portal container={container}>
      <DropdownMenuPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(contentStyles({ theme }), className)}
        {...props}
      >
        {isLoading ? <DropdownMenuSkeleton theme={theme} /> : cloneChildren(children, { theme })}
      </DropdownMenuPrimitive.Content>
    </DropdownMenuPrimitive.Portal>
  ),
);

const subTriggerStyles = cva(
  'focus:text-accent-foreground group relative flex cursor-pointer select-none h-8 px-3 items-center gap-1 text-sm outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[disabled]:text-slate-400 w-full justify-between',
  {
    variants: {
      theme: {
        dark: 'text-slate-100 focus:bg-slate-500 focus:bg-opacity-25',
        light: 'text-slate-900 focus:bg-slate-50',
      },
    },
    defaultVariants: {
      theme: ThemeEnum.Dark,
    },
  },
);

/**
 * An item that opens a submenu. Must be rendered inside `DropdownMenuSub`.
 * @see {@link https://stylus.design/components/dropdown-menu#submenus}
 */
export const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    icon?: IconDefinition;
    theme?: Theme;
  }
>(({ icon, className, theme, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(subTriggerStyles({ theme }), className)}
    {...props}
  >
    {icon && (
      <span className='dropdown-menu-item-icon flex h-4 w-4 items-center justify-center'>
        <FontAwesomeIcon
          icon={icon}
          className={cn(iconSubtleStyle({ theme }), 'text-sm transition-colors')}
        />
      </span>
    )}
    <div className='flex flex-grow px-1'>{cloneChildren(children, { theme })}</div>
    <FontAwesomeIcon icon={faCaretRight} className={iconStyles({ theme })} />
  </DropdownMenuPrimitive.SubTrigger>
));

const labelStyles = cva('group flex items-center px-4 py-1 text-sm outline-none', {
  variants: {
    theme: {
      dark: 'text-slate-100 focus:bg-slate-500 focus:bg-opacity-25',
      light: 'text-slate-900 focus:bg-slate-100 focus:bg-opacity-100',
    },
    variant: {
      title: 'font-semibold leading-tight h-8',
      label: 'text-slate-400 text-[11px] font-medium uppercase leading-none tracking-wider h-6',
    },
  },
  defaultVariants: {
    theme: 'dark',
    variant: 'label',
  },
});

/**
 * Used to render a label. It won't be focusable using arrow keys.
 * @see {@link https://stylus.design/dropdown}
 */
export const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    theme?: Theme;
    variant?: 'title' | 'label';
  }
>(({ className, theme, variant, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn(labelStyles({ theme, variant }), 'font-semibold leading-tight', className)}
    {...props}
  />
));

/**
 * Used to visually separate items in the dropdown menu.
 * @see {@link https://stylus.design/dropdown}
 */
export const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator> & {
    theme?: Theme;
  }
>(({ className, theme, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn(
      '-mx-2 my-2 h-px',
      theme === ThemeEnum.Dark ? 'bg-slate-900 drop-shadow-[0px_1px_0px_#334155]' : 'bg-slate-100',
      className,
    )}
    {...props}
  />
));
